// The RPM component bench (docs/rpm/BUILD-CHUNKS.md, chunk 0): one panel per component added for
// the RPM alerts work, with controls for its props, so every state its cdod.json declares can be
// reached by clicking rather than by reading a story file.
//
// A SIBLING of playground.tsx, never an addition to it. That file is under a ten-control contract
// asserted in three places — eslint.config.js, .dependency-cruiser.cjs and e2e/bootstrap.spec.ts —
// and a panel added there breaks all three.
//
// It carries the SAME no-data enforcement as the playground, extended to this path in both places:
// chunk 0 has no backend, every value on this page is local React state, and the enforcement is what
// keeps that true after the backend exists.
import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@chartvn/ui/button";
import { Switch } from "@chartvn/ui/switch";
import { Combobox } from "@chartvn/ui/components/combobox/combobox";
import { DataTable, type DataTableSort } from "@chartvn/ui/components/data-table/data-table";
import {
  OxygenStatusChoice,
  type OxygenStatus,
} from "@chartvn/ui/components/oxygen-status-choice/oxygen-status-choice";
import { DerivedTextBlock } from "@chartvn/ui/components/derived-text-block/derived-text-block";
import { ResidentBanner } from "@chartvn/ui/components/resident-banner/resident-banner";
import { LaneChoice } from "@chartvn/ui/components/lane-choice/lane-choice";
import {
  LaneStatusRegion,
  type AlertLane,
} from "@chartvn/ui/components/status-region/status-region";
import {
  CaptureRow,
  CaptureRowList,
  type CaptureRowBand,
  type CaptureRowPlausibility,
  type CaptureRowDetection,
  type CaptureRowExtraction,
  type CaptureRowValue,
} from "@chartvn/ui/components/capture-row/capture-row";

export const Route = createFileRoute("/_authenticated/rpm-bench")({
  // The shell reads this for the page heading and the document title (22-COMPOUNDS-SHELL §2.1).
  staticData: { screenName: "RPM component bench" },
  component: RpmBenchPage,
});

// Every value below is synthetic and fixed in this file. No real resident, facility or contact.
const ZONES = [
  { value: "America/Chicago", label: "America/Chicago", detail: "Central" },
  { value: "America/Denver", label: "America/Denver", detail: "Mountain" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles", detail: "Pacific" },
  { value: "America/New_York", label: "America/New_York", detail: "Eastern" },
  { value: "America/Phoenix", label: "America/Phoenix", detail: "Mountain, no daylight saving" },
];

const UNITS = Array.from({ length: 40 }, (_, i) => ({
  value: `unit-${i + 1}`,
  label: `Unit ${i + 1}`,
  detail: i % 2 === 0 ? "Skilled nursing" : "Assisted living",
}));

const COLUMNS = [
  { id: "resident", header: "Resident", sortable: true },
  { id: "vital", header: "Vital" },
  { id: "takenAt", header: "Taken at", sortable: true },
];

const ROWS = [
  {
    id: "r1",
    cells: { resident: "TESTERSON, Ada", vital: "SpO2 86 %", takenAt: "11-Sep-2026 08:14" },
  },
  {
    id: "r2",
    cells: {
      resident: "SAMPLEWORTH, Bernard",
      vital: "Heart rate 104 bpm",
      takenAt: "11-Sep-2026 08:31",
    },
  },
  {
    id: "r3",
    cells: {
      resident: "PLACEHOLDER-ROSSITER, Carmen Alexandra",
      vital: "Systolic 168 mmHg",
      takenAt: "11-Sep-2026 09:02",
    },
  },
];

const MANY_ROWS = Array.from({ length: 40 }, (_, i) => ({
  id: `row-${i}`,
  cells: {
    resident: `TESTERSON, Resident ${i + 1}`,
    vital: "SpO2 94 %",
    takenAt: "11-Sep-2026 08:00",
  },
}));

const SHORT_NOTE = "SpO2 86 % on room air at 08:14. Critical. Call placed to the on-call nurse.";
const LONG_NOTE = [
  "Situation: TESTERSON, Ada, Unit 3, recorded an oxygen saturation of 86 % on room air at 08:14.",
  "",
  "Background: two readings in the preceding hour were 94 % and 92 %, both clear.",
  "",
  "Assessment: the reading is critical under the room-air bands of the pinned set.",
  "",
  "Recommendation: telephone contact with the facility, this shift, and a repeat reading.",
].join("\n");

function RpmBenchPage() {
  return (
    // No <main> and no <h1>: the shell owns both. A second of either gives a screen-reader user two
    // entries for one page and sends the route-change focus move to the wrong heading.
    <div className="mx-auto flex max-w-5xl flex-col gap-8">
      <header>
        <p className="text-muted-foreground text-sm">
          One panel per RPM component. Every state each component declares is reachable from the
          controls here. Nothing on this page reads or writes data.
        </p>
      </header>

      <ComboboxPanel />
      <DataTablePanel />
      <OxygenStatusPanel />
      <DerivedTextPanel />
      <ResidentBannerPanel />
      <CaptureRowPanel />
      <LaneStatusPanel />
      <LaneChoicePanel />
    </div>
  );
}

function Panel({
  title,
  states,
  controls,
  children,
}: {
  title: string;
  states: string;
  controls: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-muted-foreground text-xs">States: {states}</p>
      </div>
      <div className="flex flex-wrap items-center gap-4 rounded-md border border-dashed p-3">
        {controls}
      </div>
      {children}
    </section>
  );
}

function Toggle({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {/* The id doubles as the test id: two panels legitimately carry a control called "Empty", so
          the visible label alone cannot address one of them. */}
      <Switch id={id} data-testid={id} checked={checked} onCheckedChange={onCheckedChange} />
      <label htmlFor={id} className="text-sm">
        {label}
      </label>
    </div>
  );
}

function ComboboxPanel() {
  const [value, setValue] = useState("");
  const [manyItems, setManyItems] = useState(false);
  const [allowFreeText, setAllowFreeText] = useState(false);
  const [disabled, setDisabled] = useState(false);

  return (
    <Panel
      title="Combobox — RPM-022, RPM-028"
      states="Default, Filtered, Empty, FreeTextEscape, ManyItems, Disabled"
      controls={
        <>
          <Toggle
            id="bench-combobox-many"
            label="Many items (40 units)"
            checked={manyItems}
            onCheckedChange={setManyItems}
          />
          <Toggle
            id="bench-combobox-freetext"
            label="Allow free text"
            checked={allowFreeText}
            onCheckedChange={setAllowFreeText}
          />
          <Toggle
            id="bench-combobox-disabled"
            label="Disabled"
            checked={disabled}
            onCheckedChange={setDisabled}
          />
          <p className="text-muted-foreground text-xs">
            Type to filter. Type something no entry matches to see the empty state, or the free-text
            escape when it is allowed.
          </p>
        </>
      }
    >
      <div className="max-w-md">
        <Combobox
          data-testid="bench-combobox"
          items={manyItems ? UNITS : ZONES}
          value={value}
          onChange={setValue}
          allowFreeText={allowFreeText}
          disabled={disabled}
          placeholder={manyItems ? "Search units" : "Search zones"}
          aria-label={manyItems ? "Resident unit" : "Operative time zone"}
        />
      </div>
      <p className="text-muted-foreground text-sm">Chosen: {value || "nothing"}</p>
    </Panel>
  );
}

function DataTablePanel() {
  const [manyRows, setManyRows] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [singleColumn, setSingleColumn] = useState(false);
  const [activatable, setActivatable] = useState(false);
  const [sort, setSort] = useState<DataTableSort | undefined>(undefined);
  const [opened, setOpened] = useState<string | undefined>(undefined);

  const rows = empty ? [] : manyRows ? MANY_ROWS : ROWS;

  return (
    <Panel
      title="DataTable — RPM-003, RPM-004, RPM-039, RPM-050, RPM-063, RPM-092"
      states="Default, Empty, Loading, LoadError, Sorted, ManyRows, SingleColumn"
      controls={
        <>
          <Toggle
            id="bench-table-many"
            label="Many rows (40)"
            checked={manyRows}
            onCheckedChange={setManyRows}
          />
          <Toggle id="bench-table-empty" label="Empty" checked={empty} onCheckedChange={setEmpty} />
          <Toggle
            id="bench-table-loading"
            label="Loading"
            checked={loading}
            onCheckedChange={setLoading}
          />
          <Toggle
            id="bench-table-error"
            label="Load error"
            checked={failed}
            onCheckedChange={setFailed}
          />
          <Toggle
            id="bench-table-single"
            label="Single column"
            checked={singleColumn}
            onCheckedChange={setSingleColumn}
          />
          <Toggle
            id="bench-table-activate"
            label="Rows open a workup"
            checked={activatable}
            onCheckedChange={setActivatable}
          />
          <Button size="sm" variant="outline" onClick={() => setSort(undefined)}>
            Clear sort
          </Button>
          <p className="text-muted-foreground text-xs">
            Activate a column header to cycle ascending, descending, then the default order.
          </p>
        </>
      }
    >
      <DataTable
        data-testid="bench-data-table"
        columns={singleColumn ? [{ id: "resident", header: "Resident" }] : COLUMNS}
        rows={rows}
        caption="Unworked readings"
        loading={loading}
        {...(failed
          ? { error: "Could not reach the server. These rows were loaded at 09:04." }
          : {})}
        {...(sort ? { sort } : {})}
        onSortChange={setSort}
        {...(activatable ? { onRowActivate: (row) => setOpened(row.id) } : {})}
      />
      <p className="text-muted-foreground text-sm">Opened row: {opened ?? "none"}</p>
    </Panel>
  );
}

function OxygenStatusPanel() {
  const [hidden, setHidden] = useState(false);
  const [required, setRequired] = useState(true);
  const [value, setValue] = useState<OxygenStatus | undefined>(undefined);

  return (
    <Panel
      title="OxygenStatusChoice — RPM-012, RPM-024"
      states="Hidden, RevealedOptional, RequiredBlocking, AnsweredRoomAir, AnsweredOnOxygen, AnswerChanged"
      controls={
        <>
          <Toggle
            id="bench-oxygen-hidden"
            label="Hidden (no SpO2 reading)"
            checked={hidden}
            onCheckedChange={setHidden}
          />
          <Toggle
            id="bench-oxygen-required"
            label="Required (the band came back gray)"
            checked={required}
            onCheckedChange={setRequired}
          />
          <Button size="sm" variant="outline" onClick={() => setValue(undefined)}>
            Clear the answer
          </Button>
        </>
      }
    >
      <OxygenStatusChoice
        data-testid="bench-oxygen-status"
        hidden={hidden}
        required={required}
        candidateBands={{ roomAir: "Critical", onOxygen: "Concerning" }}
        {...(value ? { value } : {})}
        onChange={setValue}
      />
      <p className="text-muted-foreground text-sm">Answer: {value ?? "unanswered"}</p>
    </Panel>
  );
}

function DerivedTextPanel() {
  const [long, setLong] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [copyable, setCopyable] = useState(true);
  const [stale, setStale] = useState(false);
  const [copiedAt, setCopiedAt] = useState<string | undefined>(undefined);

  return (
    <Panel
      title="DerivedTextBlock — RPM-037, RPM-047"
      states="Short, Long, Empty, Copied, Stale"
      controls={
        <>
          <Toggle id="bench-note-long" label="Long" checked={long} onCheckedChange={setLong} />
          <Toggle id="bench-note-empty" label="Empty" checked={empty} onCheckedChange={setEmpty} />
          <Toggle
            id="bench-note-copyable"
            label="Copyable"
            checked={copyable}
            onCheckedChange={setCopyable}
          />
          <Toggle id="bench-note-stale" label="Stale" checked={stale} onCheckedChange={setStale} />
        </>
      }
    >
      <DerivedTextBlock
        data-testid="bench-derived-text"
        label="Clinical note"
        text={empty ? "" : long ? LONG_NOTE : SHORT_NOTE}
        derivationNote="Assembled from the note sections recorded at 08:21."
        copyable={copyable}
        stale={stale}
        onCopy={(text) => setCopiedAt(`${text.length} characters handed to the consumer`)}
      />
      <p className="text-muted-foreground text-sm">Copy reported: {copiedAt ?? "nothing yet"}</p>
    </Panel>
  );
}

const MIRROR_STATUSES = ["none", "hospice", "discharged", "deceased"] as const;

// RPM-081's three states, in the order a building goes through them: open, closed by the mirror,
// and a reference the mirror holds no record for at all. Every value is fixed in this file.
const FACILITY_STATES = [
  { reference: "F-0142", name: "Cedar Grove Care Center", resolution: "ACTIVE" },
  { reference: "F-0142", name: "Cedar Grove Care Center", resolution: "SOFT_DELETED" },
  { reference: "F-0417", resolution: "UNRESOLVABLE" },
] as const;

function ResidentBannerPanel() {
  const [statusIndex, setStatusIndex] = useState(0);
  const [facilityIndex, setFacilityIndex] = useState(0);
  const [withBirthDate, setWithBirthDate] = useState(true);
  const [withLocation, setWithLocation] = useState(true);

  const status = MIRROR_STATUSES[statusIndex] ?? "none";
  const mirrorContext =
    status === "hospice"
      ? { hospiceSince: "01-Aug-2026" }
      : status === "discharged"
        ? { dischargedOn: "20-Aug-2026" }
        : status === "deceased"
          ? { deceasedOn: "02-Sep-2026" }
          : {};

  // The nurse picks the resident from the roster (RPM-097); the banner shows the record she picked.
  const record = {
    name: "TESTERSON, Ada",
    ...(withBirthDate ? { birthDate: "04-Mar-1952" } : {}),
    ...(withLocation ? { unit: "North 2", room: "214", bed: "A" } : {}),
  };

  const facility = FACILITY_STATES[facilityIndex] ?? FACILITY_STATES[0];

  return (
    <Panel
      title="ResidentBanner — RPM-097, RPM-084, RPM-081"
      states="Default, Hospice, Discharged, Deceased, MissingBirthDate, NoBed, Active, SoftDeleted, Unresolvable"
      controls={
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setStatusIndex((i) => (i + 1) % MIRROR_STATUSES.length)}
          >
            Mirror status: {status}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="bench-banner-facility"
            onClick={() => setFacilityIndex((i) => (i + 1) % FACILITY_STATES.length)}
          >
            Facility: {facility.resolution}
          </Button>
          <Toggle
            id="bench-banner-dob"
            label="Date of birth recorded"
            checked={withBirthDate}
            onCheckedChange={setWithBirthDate}
          />
          <Toggle
            id="bench-banner-location"
            label="Unit, room and bed recorded"
            checked={withLocation}
            onCheckedChange={setWithLocation}
          />
        </>
      }
    >
      <ResidentBanner
        data-testid="bench-resident-banner"
        record={record}
        mirrorContext={mirrorContext}
        facility={facility}
        {...(withBirthDate
          ? {}
          : { warnings: ["The mirror holds no date of birth for this resident."] })}
      />
    </Panel>
  );
}

// RPM-002's picker options, which `02-RPM-WORKFLOW.md` §4 `vp-vrow-markup` gives as
// `''/HR/RR/BP/SpO2/TEMP`. Fixed in this file: the bench reads nothing.
const CAPTURE_VITALS = [
  { value: "HR", label: "HR" },
  { value: "RR", label: "RR" },
  { value: "BP", label: "BP" },
  { value: "SPO2", label: "SpO2" },
  { value: "TEMP", label: "Temp" },
];

// The six states RPM-002 declares, the five RPM-008 adds, the four RPM-009 adds, the six RPM-011
// adds and the six RPM-012 adds, each as the rows it opens with. Every value is synthetic.
// `detections`, `extractions` and `bands` are shaped like the workup contract's answer: all of them
// are decided in the classification service and the client decides none of them (each block's own
// API row).
//
// ONE OF RPM-011's SIX IS PRESENTATION ONLY. `Implausible` is produced by RPM-010's plausibility
// bounds and renders from a fixture here. What the surface does with it — name it, and never let an
// unscored row read as a band — is what this bench shows.
//
// THE GRAY BAND IS NOT WRITTEN DOWN, HERE OR ANYWHERE. `GRAY_SPO2` below carries what the server
// sends for one worked gray reading: the band, the two candidate bands as words, the question, and
// which band each answer resolves to. Those are the classification service's answer, derived there
// from the `classification_bands` rows of the pinned set, and this file is a fixture standing in for
// one response until the workup contract exists (RPM-098/RPM-100, chunk 2). No boundary of the gray
// band appears in it.
const GRAY_SPO2: CaptureRowBand & {
  resolves: Record<OxygenStatus, "CRITICAL" | "CONCERNING" | "CLEAR">;
} = {
  band: "GRAY",
  vitalLabel: "SpO2",
  candidates: { roomAir: "Critical", onOxygen: "Concerning" },
  blockedReason: "86 % is critical on room air and concerning on oxygen — which is it?",
  resolves: { ROOM_AIR: "CRITICAL", ON_OXYGEN: "CONCERNING" },
};

/** Which picker value is the oxygen-saturation vital. Reference data in production; fixed here. */
const OXYGEN_VITALS = ["SPO2"];

const CAPTURE_STATES: Array<{
  name: string;
  rows: CaptureRowValue[];
  unresolved?: Record<string, string>;
  detections?: Record<string, CaptureRowDetection>;
  extractions?: Record<string, CaptureRowExtraction>;
  bands?: Record<string, CaptureRowBand>;
  plausibilities?: Record<string, CaptureRowPlausibility>;
  /** Empty on every state before RPM-012's six, so the oxygen question appears on none of them. */
  oxygenVitals?: string[];
  oxygenStatuses?: Record<string, OxygenStatus>;
  /** The gray row on this state, so choosing a status re-scores it to the matching candidate. */
  resolvable?: Record<string, Record<OxygenStatus, "CRITICAL" | "CONCERNING" | "CLEAR">>;
  reVitals?: boolean;
  blockedMessage?: string;
}> = [
  {
    name: "Default",
    rows: [
      { id: "b1", value: "92", unit: "/min", vitalCode: "HR" },
      { id: "b2", value: "18", unit: "/min", vitalCode: "RR" },
      { id: "b3", value: "96", unit: "%", vitalCode: "SPO2" },
    ],
  },
  {
    name: "BloodPressurePairComplete",
    rows: [{ id: "b4", value: "142/88", unit: "mmHg", vitalCode: "BP" }],
  },
  {
    name: "BloodPressureSystolicOnly",
    rows: [{ id: "b5", value: "142/", unit: "mmHg", vitalCode: "BP" }],
  },
  {
    name: "BloodPressureDiastolicOnly",
    rows: [{ id: "b6", value: "/88", unit: "mmHg", vitalCode: "BP" }],
  },
  {
    name: "UnitUnresolvable",
    rows: [{ id: "b7", value: "37.2", unit: "furlongs", vitalCode: "TEMP" }],
    unresolved: { b7: "furlongs" },
  },
  { name: "Empty", rows: [{ id: "b8", value: "", unit: "", vitalCode: "" }] },
  {
    name: "AutoDetected",
    rows: [{ id: "b9", value: "HR 98", unit: "/min", vitalCode: "HR" }],
    detections: { b9: { resolution: "EXPLICIT_WORD", label: "HR", matchedWord: "HR" } },
  },
  {
    name: "AmbiguousPickerOpen",
    rows: [{ id: "b10", value: "98", unit: "", vitalCode: "" }],
    detections: { b10: { resolution: "AMBIGUOUS" } },
    blockedMessage: 'Which vital is this? Row 1 — "98"',
  },
  {
    name: "NursePicked",
    rows: [{ id: "b11", value: "98", unit: "%", vitalCode: "SPO2" }],
    detections: { b11: { resolution: "NURSE_PICKED", label: "SpO2" } },
  },
  {
    name: "OxygenBeforeHeartRate",
    rows: [{ id: "b12", value: "pulse ox 98", unit: "%", vitalCode: "SPO2" }],
    detections: {
      b12: {
        resolution: "EXPLICIT_WORD",
        label: "SpO2",
        matchedWord: "pulse ox",
        rejected: ["HR"],
      },
    },
  },
  {
    name: "Blocked",
    rows: [
      { id: "b13", value: "HR 98", unit: "/min", vitalCode: "HR" },
      { id: "b14", value: "98", unit: "", vitalCode: "" },
    ],
    detections: {
      b13: { resolution: "EXPLICIT_WORD", label: "HR", matchedWord: "HR" },
      b14: { resolution: "AMBIGUOUS" },
    },
    blockedMessage: 'Which vital is this? Row 2 — "98"',
  },
  // RPM-009's four. The parsed number sits beside the text it was taken from, so a wrong parse is
  // visible before it is scored.
  {
    name: "Parsed",
    rows: [{ id: "b15", value: "94", unit: "%", vitalCode: "SPO2" }],
    extractions: { b15: { numberText: "94" } },
  },
  {
    name: "LabelStripped",
    rows: [{ id: "b16", value: "SpO2 94", unit: "%", vitalCode: "SPO2" }],
    extractions: { b16: { numberText: "94", strippedLabel: "SpO2" } },
  },
  {
    name: "Unparseable",
    rows: [{ id: "b17", value: "temp", unit: "", vitalCode: "TEMP" }],
    extractions: { b17: { blockedMessage: 'No number found in "temp"' } },
    blockedMessage: 'No number found in "temp"',
  },
  {
    name: "MultipleNumbers",
    // One reading and a time; a range is refused, not taken from (`J-427`).
    rows: [{ id: "b18", value: "HR 72 at 0800", unit: "/min", vitalCode: "HR" }],
    extractions: { b18: { numberText: "72" } },
  },
  // RPM-011's six. The band is text on the row and the colour only echoes it, because severity is
  // never conveyed by colour alone.
  {
    name: "Critical",
    rows: [{ id: "b19", value: "132", unit: "/min", vitalCode: "HR" }],
    bands: { b19: { band: "CRITICAL", vitalLabel: "HR" } },
  },
  {
    name: "Concerning",
    rows: [{ id: "b20", value: "104", unit: "/min", vitalCode: "HR" }],
    bands: { b20: { band: "CONCERNING", vitalLabel: "HR" } },
  },
  {
    name: "Clear",
    rows: [{ id: "b21", value: "72", unit: "/min", vitalCode: "HR" }],
    bands: { b21: { band: "CLEAR", vitalLabel: "HR" } },
  },
  {
    name: "Gray",
    rows: [{ id: "b22", value: "91", unit: "%", vitalCode: "SPO2" }],
    bands: { b22: { band: "GRAY", vitalLabel: "SpO2" } },
  },
  {
    name: "Implausible",
    rows: [{ id: "b23", value: "912", unit: "/min", vitalCode: "HR" }],
    bands: {
      b23: {
        band: null,
        vitalLabel: "HR",
        notScoredReason: "HR — outside the recordable range, so nothing was scored.",
      },
    },
  },
  {
    name: "RespiratoryRateTwoBands",
    rows: [
      { id: "b24", value: "8", unit: "/min", vitalCode: "RR" },
      { id: "b25", value: "16", unit: "/min", vitalCode: "RR" },
      { id: "b26", value: "28", unit: "/min", vitalCode: "RR" },
    ],
    bands: {
      b24: { band: "CRITICAL", vitalLabel: "RR" },
      b25: { band: "CLEAR", vitalLabel: "RR" },
      b26: { band: "CRITICAL", vitalLabel: "RR" },
    },
  },
  // RPM-010's five. A reading outside its vital's plausibility bounds is recorded and never scored:
  // the value is struck through, a text marker beside it says so in words, and the workup asks for
  // the vitals again. Neither marker is a severity colour — a device fault is not a clinical
  // finding, and the block says it must not be styled as one.
  {
    name: "AllPlausible",
    rows: [
      { id: "b27", value: "72", unit: "/min", vitalCode: "HR" },
      { id: "b28", value: "16", unit: "/min", vitalCode: "RR" },
    ],
    plausibilities: { b27: { status: "PLAUSIBLE" }, b28: { status: "PLAUSIBLE" } },
    bands: {
      b27: { band: "CLEAR", vitalLabel: "HR" },
      b28: { band: "CLEAR", vitalLabel: "RR" },
    },
  },
  {
    name: "OneImplausible",
    rows: [
      { id: "b29", value: "132", unit: "/min", vitalCode: "HR" },
      { id: "b30", value: "16", unit: "/min", vitalCode: "RR" },
      { id: "b31", value: "912", unit: "/min", vitalCode: "HR" },
    ],
    plausibilities: {
      b29: { status: "PLAUSIBLE" },
      b30: { status: "PLAUSIBLE" },
      b31: { status: "IMPLAUSIBLE" },
    },
    bands: {
      b29: { band: "CRITICAL", vitalLabel: "HR" },
      b30: { band: "CLEAR", vitalLabel: "RR" },
      b31: { band: null, vitalLabel: "HR" },
    },
    reVitals: true,
  },
  {
    name: "NonePlausible",
    rows: [
      { id: "b32", value: "912", unit: "/min", vitalCode: "HR" },
      { id: "b33", value: "0.4", unit: "/min", vitalCode: "RR" },
    ],
    plausibilities: { b32: { status: "IMPLAUSIBLE" }, b33: { status: "IMPLAUSIBLE" } },
    bands: {
      b32: { band: null, vitalLabel: "HR" },
      b33: { band: null, vitalLabel: "RR" },
    },
    reVitals: true,
  },
  {
    name: "ImplausibleBoundaryLow",
    rows: [{ id: "b34", value: "14", unit: "/min", vitalCode: "HR" }],
    plausibilities: { b34: { status: "IMPLAUSIBLE" } },
    bands: { b34: { band: null, vitalLabel: "HR" } },
    reVitals: true,
  },
  {
    name: "ImplausibleBoundaryHigh",
    rows: [{ id: "b35", value: "221", unit: "/min", vitalCode: "HR" }],
    plausibilities: { b35: { status: "IMPLAUSIBLE" } },
    bands: { b35: { band: null, vitalLabel: "HR" } },
    reVitals: true,
  },
  // RPM-012's six. The oxygen question is revealed on a row of the oxygen-saturation vital and on
  // no other row, and it is required exactly where the band came back gray.
  {
    name: "NoSpo2",
    rows: [
      { id: "b36", value: "92", unit: "/min", vitalCode: "HR" },
      { id: "b37", value: "18", unit: "/min", vitalCode: "RR" },
    ],
    bands: {
      b36: { band: "CLEAR", vitalLabel: "HR" },
      b37: { band: "CLEAR", vitalLabel: "RR" },
    },
    oxygenVitals: OXYGEN_VITALS,
  },
  {
    name: "DecidableClear",
    rows: [{ id: "b38", value: "94", unit: "%", vitalCode: "SPO2" }],
    bands: { b38: { band: "CLEAR", vitalLabel: "SpO2" } },
    oxygenVitals: OXYGEN_VITALS,
  },
  {
    name: "DecidableCritical",
    rows: [{ id: "b39", value: "84", unit: "%", vitalCode: "SPO2" }],
    bands: { b39: { band: "CRITICAL", vitalLabel: "SpO2" } },
    oxygenVitals: OXYGEN_VITALS,
  },
  {
    name: "GrayUnchosen",
    rows: [{ id: "b40", value: "86", unit: "%", vitalCode: "SPO2" }],
    bands: { b40: GRAY_SPO2 },
    oxygenVitals: OXYGEN_VITALS,
    resolvable: { b40: GRAY_SPO2.resolves },
  },
  {
    name: "GrayChosenRoomAir",
    rows: [{ id: "b41", value: "86", unit: "%", vitalCode: "SPO2" }],
    bands: { b41: GRAY_SPO2 },
    oxygenVitals: OXYGEN_VITALS,
    oxygenStatuses: { b41: "ROOM_AIR" },
    resolvable: { b41: GRAY_SPO2.resolves },
  },
  {
    name: "GrayChosenOnOxygen",
    rows: [{ id: "b42", value: "86", unit: "%", vitalCode: "SPO2" }],
    bands: { b42: GRAY_SPO2 },
    oxygenVitals: OXYGEN_VITALS,
    oxygenStatuses: { b42: "ON_OXYGEN" },
    resolvable: { b42: GRAY_SPO2.resolves },
  },
];

function CaptureRowPanel() {
  const [stateIndex, setStateIndex] = useState(0);
  const [announced, setAnnounced] = useState<string | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, OxygenStatus>>({});
  const [single, setSingle] = useState<CaptureRowValue>({
    id: "solo",
    value: "",
    unit: "",
    vitalCode: "",
  });

  const state = CAPTURE_STATES[stateIndex] ?? CAPTURE_STATES[0]!;

  // The state's own answers first, then anything clicked since. The `key` below rebuilds the list on
  // a state change, and this resets the clicks with it.
  const oxygenStatuses = { ...(state.oxygenStatuses ?? {}), ...answers };

  // RE-SCORING, WHICH IS WHAT THE ANSWER BUYS. In production the workup contract carries the band
  // and the classification service derives it from the pinned set; here the state's own fixture says
  // which band each answer resolves to, so the block lifting and the band moving are one click on
  // this page rather than two screenshots in a document.
  const bands = Object.fromEntries(
    Object.entries(state.bands ?? {}).map(([id, scored]) => {
      const answer = oxygenStatuses[id];
      const resolved = answer ? state.resolvable?.[id]?.[answer] : undefined;
      return [id, resolved ? { ...scored, band: resolved } : scored];
    }),
  );

  return (
    <Panel
      title="CaptureRow — RPM-002, RPM-008, RPM-009, RPM-010, RPM-011 and RPM-012"
      states="Default, BloodPressurePairComplete, BloodPressureSystolicOnly, BloodPressureDiastolicOnly, UnitUnresolvable, Empty, AutoDetected, AmbiguousPickerOpen, NursePicked, OxygenBeforeHeartRate, Blocked, Parsed, LabelStripped, Unparseable, MultipleNumbers, Critical, Concerning, Clear, Gray, Implausible, RespiratoryRateTwoBands, AllPlausible, OneImplausible, NonePlausible, ImplausibleBoundaryLow, ImplausibleBoundaryHigh, NoSpo2, DecidableClear, DecidableCritical, GrayUnchosen, GrayChosenRoomAir, GrayChosenOnOxygen"
      controls={
        <>
          <Button
            size="sm"
            variant="outline"
            data-testid="bench-capture-state"
            onClick={() => {
              setAnswers({});
              setStateIndex((i) => (i + 1) % CAPTURE_STATES.length);
            }}
          >
            State: {state.name}
          </Button>
          <p className="text-muted-foreground text-xs">
            Add a vital moves focus to the new row. Removing one announces how many remain; the last
            row cannot be removed. On an ambiguous row the picker takes focus, is marked unanswered,
            and the blocked reason is announced once. From `GrayUnchosen`, choosing an oxygen status
            lifts the block and moves the band to the candidate that answer names.
          </p>
        </>
      }
    >
      <CaptureRowList
        // The state button rebuilds the list, so each state opens with its own rows.
        key={state.name}
        data-testid="bench-capture-row-list"
        vitals={CAPTURE_VITALS}
        defaultRows={state.rows}
        bloodPressureValues={["BP"]}
        unresolvedUnits={state.unresolved ?? {}}
        detections={state.detections ?? {}}
        extractions={state.extractions ?? {}}
        bands={bands}
        plausibilities={state.plausibilities ?? {}}
        oxygenVitalValues={state.oxygenVitals ?? []}
        oxygenStatuses={oxygenStatuses}
        onOxygenStatusChange={(id, value) => setAnswers((a) => ({ ...a, [id]: value }))}
        reVitals={state.reVitals ?? false}
        blockedMessage={state.blockedMessage}
        announce={setAnnounced}
      />
      <p className="text-muted-foreground text-sm">Announced: {announced ?? "nothing yet"}</p>
      <div className="border-t pt-4">
        <p className="text-muted-foreground mb-2 text-xs">One row on its own, outside a list.</p>
        <CaptureRow
          data-testid="bench-capture-row"
          row={single}
          vitals={CAPTURE_VITALS}
          position={1}
          removable={false}
          onChange={setSingle}
        />
      </div>
    </Panel>
  );
}

// RPM-013's five states, and the oxygen fail-safe's two. Each is ONE workup-contract response,
// standing in for a contract that is not built (`J-215`, `J-265`): the lane, the driving vital and
// the fail-safe marker arrive already decided, because this component computes none of them. The
// driving-vital strings are readings, never band boundaries.
const LANE_STATES: readonly {
  name: string;
  lane: AlertLane;
  drivenBy?: string;
  scoredOnRoomAir?: boolean;
}[] = [
  { name: "CriticalDrivenByHr", lane: "CRITICAL", drivenBy: "heart rate 38" },
  { name: "ConcerningDrivenBySpo2", lane: "CONCERNING", drivenBy: "oxygen saturation 91 %" },
  { name: "Clear", lane: "CLEAR", drivenBy: "heart rate 72" },
  { name: "MachineOnly", lane: "MACHINE" },
  { name: "PartialBpPair", lane: "CONCERNING", drivenBy: "systolic 168 mmHg" },
  // The fail-safe fired: nobody said whether the resident was on oxygen, so the reading was scored
  // against room air — the more conservative table — and the banner says so in the same sentence.
  {
    name: "FailsafeFired",
    lane: "CONCERNING",
    drivenBy: "oxygen saturation 91 %",
    scoredOnRoomAir: true,
  },
  // The same lane and the same reading, with the status answered. No note, because nothing was
  // assumed. Put the two side by side and the note is the whole of the difference.
  { name: "FailsafeNotNeeded", lane: "CONCERNING", drivenBy: "oxygen saturation 91 %" },
];

function LaneStatusPanel() {
  const [index, setIndex] = useState(0);
  // Undefined until the first click, which is the third clause of the politeness rule: a first
  // announcement is a state and not a change, so it is announced on its own obligation.
  const [previousLane, setPreviousLane] = useState<AlertLane | undefined>(undefined);

  const state = LANE_STATES[index] ?? LANE_STATES[0]!;

  // READ OFF THE REGION, NEVER RECOMPUTED. A second copy of the politeness rule here is how the page
  // and the region come to disagree, and the page is the thing a reviewer believes. So the line
  // below reports what the region actually did: `data-announce` carries the same decision as
  // `aria-live`, and this reads it after each render.
  const regionRef = useRef<HTMLDivElement>(null);
  const [announced, setAnnounced] = useState<string>("");
  const [failsafe, setFailsafe] = useState<string>("");
  useEffect(() => {
    setAnnounced(regionRef.current?.getAttribute("data-announce") ?? "");
    setFailsafe(regionRef.current?.getAttribute("data-failsafe") ?? "");
  }, [index]);

  return (
    <Panel
      title="StatusRegion — RPM-013 and the oxygen fail-safe"
      states={LANE_STATES.map((s) => s.name).join(", ")}
      controls={
        <>
          <Button
            size="sm"
            variant="outline"
            data-testid="bench-lane-state"
            onClick={() => {
              setPreviousLane(state.lane);
              setIndex((i) => (i + 1) % LANE_STATES.length);
            }}
          >
            State: {state.name}
          </Button>
          <p className="text-muted-foreground text-xs">
            Each click leaves the lane you were on, so the next announcement is a CHANGE. A lane
            that worsens interrupts the screen reader; one that improves waits its turn. The line
            below says which happened, and the region carries it as `aria-live`. The last two states
            are the same lane and the same reading, once with the oxygen status missing and once
            with it given: the note is the whole of the difference.
          </p>
        </>
      }
    >
      <LaneStatusRegion
        ref={regionRef}
        data-testid="bench-lane-status"
        lane={state.lane}
        {...(state.drivenBy === undefined ? {} : { drivenBy: state.drivenBy })}
        {...(previousLane === undefined ? {} : { previousLane })}
        {...(state.scoredOnRoomAir === undefined ? {} : { scoredOnRoomAir: state.scoredOnRoomAir })}
      />
      <p className="text-muted-foreground text-sm" data-testid="bench-lane-failsafe">
        {failsafe === "room-air"
          ? "Oxygen status not given, so the reading was scored against room air — the more conservative of the two tables. The note is part of this announcement, not a second one."
          : "Every reading scored on a status that was given, so no fail-safe was needed."}
      </p>
      <p className="text-muted-foreground text-sm" data-testid="bench-lane-direction">
        {previousLane === undefined
          ? `First announcement, no previous lane: announced ${
              announced === "assertive" ? "assertively" : "politely"
            } on its own obligation.`
          : `${previousLane} to ${state.lane}: ${
              announced === "assertive"
                ? "worse, announced assertively"
                : "not worse, announced politely"
            }.`}
      </p>
    </Panel>
  );
}

// RPM-015's six states. Each pair is one workup-contract response: what the classifier computed,
// and what stands. The two agree on the first four, differ on the fifth, and the sixth is the same
// control with nothing operable. No band, no boundary and no reading appears here — the lane is a
// VALUE, and the panel below carries nothing else.
const LANE_CHOICE_STATES: readonly {
  name: string;
  value: AlertLane;
  computedValue: AlertLane;
  disabled?: boolean;
}[] = [
  { name: "Critical", value: "CRITICAL", computedValue: "CRITICAL" },
  { name: "Concerning", value: "CONCERNING", computedValue: "CONCERNING" },
  { name: "Clear", value: "CLEAR", computedValue: "CLEAR" },
  { name: "Machine", value: "MACHINE", computedValue: "MACHINE" },
  { name: "OverriddenFromCritical", value: "CONCERNING", computedValue: "CRITICAL" },
  { name: "Disabled", value: "CLEAR", computedValue: "CLEAR", disabled: true },
];

function LaneChoicePanel() {
  const [index, setIndex] = useState(0);
  const state = LANE_CHOICE_STATES[index] ?? LANE_CHOICE_STATES[0]!;
  // The nurse's pick, cleared whenever the state changes: each state is its own workup.
  const [picked, setPicked] = useState<AlertLane | null>(null);

  const value = picked ?? state.value;

  // READ OFF THE CONTROL AND OFF THE REGION, NEVER RECOMPUTED. The lane-to-token mapping exists in
  // two files — `lane-choice.tsx` draws the chosen segment, `status-region.tsx` tints the
  // announcement — and a second copy is how two surfaces come to disagree about one lane. Both are
  // on this page, so the agreement is read from the DOM and asserted by the e2e spec rather than
  // left to review. `data-tone` carries each component's own answer.
  const choiceRef = useRef<HTMLFieldSetElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const [tones, setTones] = useState<{ choice: string; region: string }>({
    choice: "",
    region: "",
  });
  const [announces, setAnnounces] = useState<string>("");
  useEffect(() => {
    setTones({
      choice: choiceRef.current?.getAttribute("data-tone") ?? "",
      region: regionRef.current?.getAttribute("data-tone") ?? "",
    });
    setAnnounces(choiceRef.current?.getAttribute("data-announces") ?? "");
  }, [index, picked]);

  return (
    <Panel
      title="LaneChoice — RPM-015, the lane vocabulary"
      states={LANE_CHOICE_STATES.map((s) => s.name).join(", ")}
      controls={
        <>
          <Button
            size="sm"
            variant="outline"
            data-testid="bench-lane-choice-state"
            onClick={() => {
              setPicked(null);
              setIndex((i) => (i + 1) % LANE_CHOICE_STATES.length);
            }}
          >
            State: {state.name}
          </Button>
          <p className="text-muted-foreground text-xs">
            Four options and no fifth: gray is a reading-level band, so it is not in this vocabulary
            and cannot be selected. Machine is a lane like the other three — a re-vitals workflow,
            not an error. Pick an option that is not the computed one and the control announces an
            override rather than a selection, while the computed lane stays on screen.
          </p>
        </>
      }
    >
      <div data-testid="bench-lane-choice">
        <LaneChoice
          ref={choiceRef}
          value={value}
          computedValue={state.computedValue}
          onChange={setPicked}
          {...(state.disabled === undefined ? {} : { disabled: state.disabled })}
        />
      </div>
      <LaneStatusRegion
        ref={regionRef}
        data-testid="bench-lane-choice-region"
        lane={value}
        previousLane={value}
      />
      <p className="text-muted-foreground text-sm" data-testid="bench-lane-choice-announces">
        {announces === "override"
          ? "This lane is not the one the system computed, so the chosen option reads as an override of it."
          : "This lane is the one the system computed, so the chosen option reads as the default."}
      </p>
      <p className="text-muted-foreground text-sm" data-testid="bench-lane-choice-tone">
        {tones.choice && tones.choice === tones.region
          ? `Control and announcement both map this lane to the ${tones.choice} token pair.`
          : `Control maps this lane to ${tones.choice || "nothing"}, announcement to ${tones.region || "nothing"}.`}
      </p>
    </Panel>
  );
}
